import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import tw from "twin.macro";
import styled from "styled-components";
import AnimationRevealPage from "helpers/AnimationRevealPage.js";
import Footer from "components/footers/FourColumnWithInputForm.js";
import Header from "components/headers/HeaderLight";
import { SectionHeading } from "components/misc/Headings.js";
import { useOwners } from "hooks/useOwners.js";
import { usePayments } from "hooks/usePayments.js";
import { useAmenities } from "hooks/useAmenities.js";
import { useAccounting } from "hooks/useAccounting.js";
import { useFraccs } from "hooks/useFraccs.js";
import { ROUTE_PATHS } from "utils/routes";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line
} from "recharts";

const Container = tw.div`relative`;
const Heading = tw(
  SectionHeading
)`mt-4 font-black text-3xl sm:text-4xl lg:text-5xl text-center leading-tight`;

// Dashboard Cards
const DashboardGrid = tw.div`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mt-8 mb-12`;
const DashboardCard = tw.div`bg-white backdrop-blur-md border border-blue-300 shadow-2xl rounded-3xl p-6 hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:-translate-y-1 opacity-75`;
const CardIcon = tw.div`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center text-3xl`;
const CardTitle = tw.h3`text-xl font-bold text-gray-800 mb-2 text-center`;
const CardValue = tw.p`text-3xl font-bold text-center mb-2`;
const CardDescription = tw.p`text-gray-600 text-center text-sm`;

// Status Badge for various states
const StatusBadge = styled.span(({ variant }) => [
  tw`px-3 py-1 rounded-full text-xs font-bold`,
  variant === "success" && tw`bg-green-100 text-green-800`,
  variant === "warning" && tw`bg-yellow-100 text-yellow-800`,
  variant === "danger" && tw`bg-red-100 text-red-800`,
  variant === "info" && tw`bg-blue-100 text-blue-800`,
]);

// Quick Actions Section
const QuickActionsGrid = tw.div`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-8`;
  const QuickActionCard = tw.div`bg-white rounded-lg shadow-xl p-4 text-center hover:shadow-2xl transition-all duration-300 cursor-pointer`;
const ActionTitle = tw.h4`text-lg font-bold mb-2`;
const ActionDescription = tw.p`text-primary-100 text-sm`;

// Recent Activity Section
const ActivitySection = tw.div`bg-white backdrop-blur-md border border-blue-300 shadow-2xl rounded-3xl p-6 mt-8 opacity-75`;
const ActivityTitle = tw.h3`text-2xl font-bold text-gray-800 mb-6`;
const ActivityItem = tw.div`flex items-center justify-between py-3 border-b border-gray-200 last:border-b-0`;
const ActivityText = tw.p`text-gray-700`;
const ActivityTime = tw.span`text-gray-500 text-sm`;

// Summary Cards Section
const SummarySection = tw.div`mt-12`;
const SummaryGrid = tw.div`grid grid-cols-1 lg:grid-cols-2 gap-8 mt-6`;
const SummaryCard = tw.div`bg-white backdrop-blur-md border border-blue-300 shadow-2xl rounded-3xl p-6 opacity-75`;
const SummaryTitle = tw.h4`text-xl font-bold text-gray-800 mb-4`;

// Chart Containers
const ChartContainer = styled.div`
  width: 100%;
  height: 16rem;
  margin-top: 1rem;
  min-height: 250px;
`;
const ChartSection = tw.div`bg-white backdrop-blur-md border border-blue-300 shadow-2xl rounded-3xl p-6 mt-8 opacity-75`;

const AdminDashboardPage = () => {
  const navigate = useNavigate();
  const { owners, fetchOwners } = useOwners();
  const { allPayments, fetchAllPayments } = usePayments();
  const { amenities, reservations, fetchAmenities, getAllReservations } = useAmenities();
  const { operations, periods, fetchOperations } = useAccounting();
  const { fraccs } = useFraccs();
  
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    totalOwners: 20,
    morosoOwners: 5,
    totalPayments: 30,
    pendingPayments: 10,
    approvedPayments: 10,
    totalAmenities: 6,
    pendingReservations: 2,
    totalFraccs: 3,
    openPeriods: 2,
    totalIncome: 285000,
    totalOutcome: 165000
  });

  useEffect(() => {
    const loadDashboardData = async () => {
      setLoading(true);
      try {
        await Promise.all([
          fetchOwners(),
          fetchAllPayments(),
          fetchAmenities(),
          getAllReservations(),
          fetchOperations()
        ]);
      } catch (error) {
        console.error('Error loading dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Always use realistic mock data for charts - calculate from the expanded mock data
    const mockData = {
      totalOwners: 20, // From expanded mockData owners array
      morosoOwners: 5, // 5 moroso owners from expanded mockData
      totalPayments: 30, // From expanded mockData payments array
      pendingPayments: 10, // 10 pending payments (status: 0)
      approvedPayments: 10, // 10 approved payments (status: 1)
      totalAmenities: 6,
      pendingReservations: 2,
      totalFraccs: 3,
      openPeriods: 2,
      totalIncome: 285000, // Higher income to show in charts
      totalOutcome: 165000 // Expenses to show in charts
    };

    setDashboardData(mockData);
  }, []); // Empty dependency array to run only once

  const handleCardClick = (route) => {
    navigate(route);
  };

  // Chart data preparation
  // Always show at least a small value for the bar chart
  const financialChartData = [
    {
      name: 'Ingresos',
      value: dashboardData.totalIncome || 285000,
      color: '#10B981'
    },
    {
      name: 'Egresos',
      value: dashboardData.totalOutcome || 165000,
      color: '#EF4444'
    }
  ];


  const statusChartData = [
    {
      name: 'Pagos Aprobados',
      value: dashboardData.approvedPayments || 10,
      color: '#10B981'
    },
    {
      name: 'Pagos Pendientes',
      value: dashboardData.pendingPayments || 10,
      color: '#F59E0B'
    }
  ];

  const ownersStatusData = [
    {
      name: 'Propietarios al Corriente',
      value: (dashboardData.totalOwners - dashboardData.morosoOwners) || 15,
      color: '#10B981'
    },
    {
      name: 'Propietarios Morosos',
      value: dashboardData.morosoOwners || 5,
      color: '#EF4444'
    }
  ];

  const COLORS = ['#10B981', '#EF4444', '#F59E0B', '#8B5CF6', '#06B6D4'];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-300 rounded-lg shadow-lg">
          <p className="font-semibold">{`${payload[0].name}: $${payload[0].value?.toLocaleString()}`}</p>
        </div>
      );
    }
    return null;
  };

  const PieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-300 rounded-lg shadow-lg">
          <p className="font-semibold">{`${payload[0].name}: ${payload[0].value}`}</p>
        </div>
      );
    }
    return null;
  };

  const quickActions = [
    {
      title: "Nuevo Propietario",
      description: "Registrar un nuevo residente o propietario y asignarlo a su coto",
      action: () => navigate(ROUTE_PATHS.ADMINOWNERSPAGE),
      gradient: "from-blue-500 to-blue-700"
    },
    {
      title: "Registrar Pago",
      description: "Aprobar o registrar un cobro de mantenimiento pendiente",
      action: () => navigate(ROUTE_PATHS.ADMINPAYMENTPAGE),
      gradient: "from-green-500 to-green-700"
    },
    {
      title: "Nueva Amenidad",
      description: "Agregar un espacio común o gestionar los existentes",
      action: () => navigate(ROUTE_PATHS.ADMINAMENITIESPAGE),
      gradient: "from-purple-500 to-purple-700"
    },
    {
      title: "Publicar Anuncio",
      description: "Redactar y enviar un comunicado a todos los residentes",
      action: () => navigate(ROUTE_PATHS.ADMINANNOUNCEMENTSPAGE),
      gradient: "from-yellow-500 to-yellow-700"
    },
    {
      title: "Nuevo Periodo Contable",
      description: "Abrir un nuevo ciclo de cobranza y asignar cuotas automáticamente",
      action: () => navigate(ROUTE_PATHS.ADMINACCOUNTINGPAGE),
      gradient: "from-red-500 to-red-700"
    },
    {
      title: "Gestionar Catálogos",
      description: "Administrar las categorías de pagos, anuncios y conceptos contables",
      action: () => navigate(ROUTE_PATHS.ADMINCATALOGSPAGE),
      gradient: "from-indigo-500 to-indigo-700"
    }
  ];

  const recentActivities = [
    { text: "Nuevo propietario registrado: María González (Casa #203)", time: "Hace 2 horas" },
    { text: "Pago aprobado para Casa #105 - $5,200 MXN", time: "Hace 3 horas" },
    { text: "Reserva de alberca confirmada para el 15 de Oct", time: "Hace 4 horas" },
    { text: "Propietario de Casa #78 marcado como moroso", time: "Hace 6 horas" },
    { text: "Nueva amenidad agregada: Cancha de Tenis", time: "Hace 1 día" },
    { text: "Periodo contable de Septiembre cerrado exitosamente", time: "Hace 2 días" },
    { text: "Egreso registrado: Mantenimiento de jardinería - $8,500 MXN", time: "Hace 3 días" },
    { text: "15 pagos de mantenimiento procesados automáticamente", time: "Hace 4 días" }
  ];

  if (loading) {
    return (
      <AnimationRevealPage>
        <Header />
        <Container className="py-12">
          <Heading>Cargando panel de administración…</Heading>
        </Container>
        <Footer />
      </AnimationRevealPage>
    );
  }

  return (
    <AnimationRevealPage>
      <Header />
      <Container className="py-12">
        <Heading>Panel de Control Administrativo</Heading>
        <p className="text-center text-gray-600 mt-4 mb-8">
          Vista general del estado del fraccionamiento: propietarios, pagos, amenidades y contabilidad.
        </p>

        {/* Main Metrics Dashboard */}
        <DashboardGrid>
          {/* Owners Card */}
          <DashboardCard onClick={() => handleCardClick(ROUTE_PATHS.ADMINOWNERSPAGE)}>
            <CardIcon className="bg-blue-100 text-blue-600">
              👥
            </CardIcon>
            <CardTitle>Propietarios</CardTitle>
            <CardValue className="text-blue-600">{dashboardData.totalOwners}</CardValue>
            <CardDescription>
              {dashboardData.morosoOwners > 0 && (
                <StatusBadge variant="danger">
                  {dashboardData.morosoOwners} morosos
                </StatusBadge>
              )}
            </CardDescription>
          </DashboardCard>

          {/* Payments Card */}
          <DashboardCard onClick={() => handleCardClick(ROUTE_PATHS.ADMINPAYMENTPAGE)}>
            <CardIcon className="bg-green-100 text-green-600">
              💰
            </CardIcon>
            <CardTitle>Pagos</CardTitle>
            <CardValue className="text-green-600">{dashboardData.totalPayments}</CardValue>
            <CardDescription>
              <StatusBadge variant="warning">
                {dashboardData.pendingPayments} pendientes
              </StatusBadge>
            </CardDescription>
          </DashboardCard>

          {/* Amenities Card */}
          <DashboardCard onClick={() => handleCardClick(ROUTE_PATHS.ADMINAMENITIESPAGE)}>
            <CardIcon className="bg-purple-100 text-purple-600">
              🏊‍♂️
            </CardIcon>
            <CardTitle>Amenidades</CardTitle>
            <CardValue className="text-purple-600">{dashboardData.totalAmenities}</CardValue>
            <CardDescription>
              {dashboardData.pendingReservations > 0 && (
                <StatusBadge variant="info">
                  {dashboardData.pendingReservations} reservas pendientes
                </StatusBadge>
              )}
            </CardDescription>
          </DashboardCard>

          {/* Fraccionamientos Card */}
          <DashboardCard onClick={() => handleCardClick(ROUTE_PATHS.ADMINOWNERSPAGE)}>
            <CardIcon className="bg-yellow-100 text-yellow-600">
              🏘️
            </CardIcon>
            <CardTitle>Fraccionamientos</CardTitle>
            <CardValue className="text-yellow-600">{dashboardData.totalFraccs}</CardValue>
            <CardDescription>Cotos registrados</CardDescription>
          </DashboardCard>

          {/* Accounting Income Card */}
          <DashboardCard onClick={() => handleCardClick(ROUTE_PATHS.ADMINACCOUNTINGPAGE)}>
            <CardIcon className="bg-green-100 text-green-600">
              📈
            </CardIcon>
            <CardTitle>Ingresos</CardTitle>
            <CardValue className="text-green-600">${dashboardData.totalIncome.toLocaleString()}</CardValue>
            <CardDescription>Total de ingresos</CardDescription>
          </DashboardCard>

          {/* Accounting Outcome Card */}
          <DashboardCard onClick={() => handleCardClick(ROUTE_PATHS.ADMINACCOUNTINGPAGE)}>
            <CardIcon className="bg-red-100 text-red-600">
              📉
            </CardIcon>
            <CardTitle>Egresos</CardTitle>
            <CardValue className="text-red-600">${dashboardData.totalOutcome.toLocaleString()}</CardValue>
            <CardDescription>Total de egresos</CardDescription>
          </DashboardCard>

          {/* Periods Card */}
          <DashboardCard onClick={() => handleCardClick(ROUTE_PATHS.ADMINACCOUNTINGPAGE)}>
            <CardIcon className="bg-indigo-100 text-indigo-600">
              📅
            </CardIcon>
            <CardTitle>Periodos</CardTitle>
            <CardValue className="text-indigo-600">{dashboardData.openPeriods}</CardValue>
            <CardDescription>Periodos abiertos</CardDescription>
          </DashboardCard>

          {/* Balance Card */}
          <DashboardCard onClick={() => handleCardClick(ROUTE_PATHS.ADMINACCOUNTINGPAGE)}>
            <CardIcon className="bg-gray-100 text-gray-600">
              ⚖️
            </CardIcon>
            <CardTitle>Balance</CardTitle>
            <CardValue className={`${(dashboardData.totalIncome - dashboardData.totalOutcome) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              ${(dashboardData.totalIncome - dashboardData.totalOutcome).toLocaleString()}
            </CardValue>
            <CardDescription>Balance general</CardDescription>
          </DashboardCard>
        </DashboardGrid>

        {/* Quick Actions Section */}
        <SummarySection>
          <SummaryTitle className="text-center text-2xl font-bold text-gray-800 mb-6">
            Acciones Rápidas
          </SummaryTitle>
          <QuickActionsGrid>
            {quickActions.map((action, index) => (
              <QuickActionCard key={action.title} onClick={action.action}>
                <ActionTitle>{action.title}</ActionTitle>
                <ActionDescription>{action.description}</ActionDescription>
              </QuickActionCard>
            ))}
          </QuickActionsGrid>
        </SummarySection>

        {/* Summary Section */}
        <SummarySection>
          <SummaryGrid>
            {/* Financial Summary */}
            <SummaryCard>
              <SummaryTitle>Resumen Financiero</SummaryTitle>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Ingresos del mes:</span>
                  <span className="font-bold text-green-600">${dashboardData.totalIncome.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Egresos del mes:</span>
                  <span className="font-bold text-red-600">${dashboardData.totalOutcome.toLocaleString()}</span>
                </div>
                <div className="border-t pt-2">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-800 font-semibold">Balance neto:</span>
                    <span className={`font-bold ${(dashboardData.totalIncome - dashboardData.totalOutcome) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      ${(dashboardData.totalIncome - dashboardData.totalOutcome).toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Financial Chart */}
              <ChartContainer>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={financialChartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis tickFormatter={(value) => `$${(value/1000).toFixed(0)}K`} />
                    <Tooltip formatter={(value) => [`$${value.toLocaleString()}`, '']} />
                    <Bar dataKey="value">
                      {financialChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </ChartContainer>
            </SummaryCard>

            {/* Status Summary */}
            <SummaryCard>
              <SummaryTitle>Estado General</SummaryTitle>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Pagos pendientes:</span>
                  <StatusBadge variant={dashboardData.pendingPayments > 0 ? "warning" : "success"}>
                    {dashboardData.pendingPayments}
                  </StatusBadge>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Propietarios morosos:</span>
                  <StatusBadge variant={dashboardData.morosoOwners > 0 ? "danger" : "success"}>
                    {dashboardData.morosoOwners}
                  </StatusBadge>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Reservas pendientes:</span>
                  <StatusBadge variant={dashboardData.pendingReservations > 0 ? "info" : "success"}>
                    {dashboardData.pendingReservations}
                  </StatusBadge>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Periodos abiertos:</span>
                  <StatusBadge variant="info">
                    {dashboardData.openPeriods}
                  </StatusBadge>
                </div>
              </div>
              
              {/* Status Chart */}
              <ChartContainer>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={ownersStatusData}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      dataKey="value"
                      label={({ name, value }) => `${name}: ${value}`}
                    >
                      {ownersStatusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value, name) => [value, name]} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </ChartContainer>
            </SummaryCard>
          </SummaryGrid>
        </SummarySection>

        {/* Additional Charts Section */}
        <ChartSection>
          <SummaryTitle>Análisis de Pagos</SummaryTitle>
          <ChartContainer className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart margin={{ right: 80 }}>
                <Pie
                  data={statusChartData}
                  cx="40%"
                  cy="50%"
                  labelLine={false}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                  paddingAngle={5}
                >
                  {statusChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<PieTooltip />} />
                <Legend layout="vertical" align="right" verticalAlign="middle" iconType="rect" wrapperStyle={{ right: 0, top: 40 }} />
              </PieChart>
            </ResponsiveContainer>
          </ChartContainer>
        </ChartSection>

        {/* Recent Activity */}
        <ActivitySection>
          <ActivityTitle>Actividad Reciente</ActivityTitle>
          <div>
            {recentActivities.map((activity) => (
              <ActivityItem key={activity.text.substring(0, 20)}>
                <ActivityText>{activity.text}</ActivityText>
                <ActivityTime>{activity.time}</ActivityTime>
              </ActivityItem>
            ))}
          </div>
        </ActivitySection>
      </Container>
      <Footer />
    </AnimationRevealPage>
  );
};

export default AdminDashboardPage;